import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Loader2, CreditCard, Copy, Download, Eye, FileText, QrCode, X } from 'lucide-react';
import { TENANT_ORG_ID_STORAGE_KEY } from '../../constants/tenant';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchPricingModulesWithFallback,
  fetchUserSeats,
  fetchUserTiersWithFallback,
  getSubscription,
  patchSubscription,
  postPricingCalculate,
  createSubscription,
  chargeSubscription,
  downloadSubscriptionBoleto,
  fetchBillingHome,
  previewSubscriptionChange,
  cancelSubscriptionChange,
} from '../../services/subscriptionService';
import { useTranslation } from 'react-i18next';
import { userFacingError } from '../../i18n/userFacingError';

const onlyDigits = (v) => String(v || '').replace(/\D+/g, '');

const CYCLES = [
  { id: 'monthly', label: 'Mensal' },
  { id: 'semiannual', label: 'Semestral' },
  { id: 'annual', label: 'Anual' },
];

export default function SettingsSubscriptionTab({ headerBar }) {
  const { t } = useTranslation('billing');
  const { user } = useAuth();
  const [orgId, setOrgId] = useState(null);
  const [pricing, setPricing] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [tierCode, setTierCode] = useState('');
  const [seats, setSeats] = useState(null);
  const [sub, setSub] = useState(null);
  const [selected, setSelected] = useState(() => new Set(['base']));
  const [cycle, setCycle] = useState('monthly');
  const [previewCents, setPreviewCents] = useState(null);
  const [planChangePreview, setPlanChangePreview] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [charging, setCharging] = useState('');
  const [billingHome, setBillingHome] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [pricingFromFallback, setPricingFromFallback] = useState(false);
  const [subFetchError, setSubFetchError] = useState(false);

  const providerLabel = sub?.payment_provider_display_name || sub?.payment_provider || 'gateway de pagamento';

  useEffect(() => {
    try {
      setOrgId(localStorage.getItem(TENANT_ORG_ID_STORAGE_KEY));
    } catch {
      setOrgId(null);
    }
  }, []);

  const load = useCallback(async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setPricingFromFallback(false);
    setSubFetchError(false);
    try {
      const { list, fromFallback } = await fetchPricingModulesWithFallback();
      setPricing(list);
      setPricingFromFallback(fromFallback);
      const { list: tierList } = await fetchUserTiersWithFallback();
      setTiers(tierList);
      try {
        setSeats(await fetchUserSeats());
      } catch {
        setSeats(null);
      }
      let sr = null;
      try {
        sr = await getSubscription(orgId);
      } catch (e) {
        const st = e?.response?.status;
        if (st === 404) {
          setSubFetchError(false);
        } else {
          setSubFetchError(true);
        }
        sr = null;
      }
      if (sr?.data) {
        setSub(sr.data);
        setPendingChanges(sr.data.pending_changes || []);
        setSubFetchError(false);
        const codes = sr.data.module_codes || ['base'];
        setSelected(new Set(codes.map((c) => String(c).toLowerCase())));
        setCycle(sr.data.billing_cycle || 'monthly');
        setTierCode(sr.data.user_tier_code || '');
        try {
          const home = await fetchBillingHome();
          setBillingHome(home.data);
        } catch {
          setBillingHome(null);
        }
      } else {
        setSub(null);
        setPendingChanges([]);
        setBillingHome(null);
        setSelected(new Set(['base']));
      }
    } catch (e) {
      console.error('SettingsSubscriptionTab load', e);
      toast.error(t('loadTabError'));
    } finally {
      setLoading(false);
    }
  }, [orgId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!orgId) return;
    const codes = Array.from(selected);
    postPricingCalculate({ module_codes: codes, billing_cycle: cycle, user_tier_code: tierCode || null })
      .then((r) => {
        const n = r.data?.price_period_cents;
        setPreviewCents(typeof n === 'number' ? n : null);
      })
      .catch(() => setPreviewCents(null));
  }, [orgId, selected, cycle, tierCode]);

  useEffect(() => {
    if (!orgId || !sub) {
      setPlanChangePreview(null);
      return;
    }
    const codes = Array.from(selected);
    previewSubscriptionChange(orgId, {
      module_codes: codes,
      billing_cycle: cycle,
      user_tier_code: tierCode || null,
      confirm: false,
    })
      .then((r) => setPlanChangePreview(r.data))
      .catch(() => setPlanChangePreview(null));
  }, [orgId, sub, selected, cycle, tierCode]);

  const toggle = (code) => {
    const c = String(code).toLowerCase();
    if (c === 'base') return;
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      if (!n.has('base')) n.add('base');
      return n;
    });
  };

  const invoice = billingHome?.invoice || billingHome;
  const canPay = Boolean(sub) && sub.status !== 'cancelled' && sub.status !== 'none';

  useEffect(() => {
    if (!canPay || !(invoice?.pix_copy || invoice?.boleto_linha || invoice?.boleto_pdf_url)) return undefined;
    const timer = setInterval(() => {
      fetchBillingHome()
        .then((r) => setBillingHome(r.data))
        .catch(() => {});
    }, 8000);
    return () => clearInterval(timer);
  }, [canPay, invoice?.pix_copy, invoice?.boleto_linha, invoice?.boleto_pdf_url]);

  useEffect(() => () => {
    if (viewerUrl) URL.revokeObjectURL(viewerUrl);
  }, [viewerUrl]);

  const charge = async (method) => {
    setCharging(method);
    try {
      const { data } = await chargeSubscription(method);
      setBillingHome(data);
      toast.success(method === 'pix' ? t('waitingPix') : t('waitingBoleto'));
    } catch (e) {
      toast.error(userFacingError(e, 'chargeError'));
    } finally {
      setCharging('');
    }
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('copied'));
    } catch {
      toast.error(t('copyFailed'));
    }
  };

  const fetchBoletoBlob = async () => {
    const r = await downloadSubscriptionBoleto();
    return new Blob([r.data], { type: 'application/pdf' });
  };

  const viewBoleto = async () => {
    try {
      const blob = await fetchBoletoBlob();
      if (viewerUrl) URL.revokeObjectURL(viewerUrl);
      setViewerUrl(URL.createObjectURL(blob));
      setViewerOpen(true);
    } catch {
      toast.error(t('downloadFailed'));
    }
  };

  const downloadBoleto = async () => {
    try {
      const blob = await fetchBoletoBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'boleto-assinatura.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('downloadFailed'));
    }
  };

  const qrSrc = (b64) => (b64?.startsWith('data:') ? b64 : `data:image/png;base64,${b64 || ''}`);

  const cancelChange = async (changeId) => {
    try {
      await cancelSubscriptionChange(orgId, changeId);
      toast.success(t('changeCanceled'));
      await load();
    } catch (e) {
      toast.error(userFacingError(e, 'cancelChangeError'));
    }
  };

  const save = async () => {
    if (!orgId) {
      toast.error(t('orgMissing'));
      return;
    }
    setSaving(true);
    try {
      const codes = Array.from(selected);
      if (sub) {
        const { data } = await patchSubscription(orgId, {
          module_codes: codes,
          billing_cycle: cycle,
          user_tier_code: tierCode || null,
        });
        setSub(data);
        setPendingChanges(data.pending_changes || []);
        const msg = data.change_preview || 'Plano atualizado.';
        toast.success(msg);
        return;
      }
      const email = (user?.email || user?.company_email || '').trim() || null;
      const orgName =
        (user?.company_name || user?.name || user?.username || '').trim() || null;
      const cnpjDigits = onlyDigits(user?.company_cnpj);
      const { data } = await createSubscription({
        organization_id: orgId,
        module_codes: codes,
        billing_cycle: cycle,
        user_tier_code: tierCode || null,
        trial_days: 0,
        organization_name: orgName,
        organization_email: email,
        organization_cnpj: cnpjDigits.length === 14 ? cnpjDigits : null,
      });
      setSub(data);
      setSubFetchError(false);
      toast.success(t('subscriptionCreated'));
    } catch (e) {
      const st = e?.response?.status;
      if (st === 409) {
        try {
          await load();
          toast(t('subscriptionExists'), { icon: 'ℹ️' });
        } catch {
          toast.error(t('subscriptionExists'));
        }
        return;
      }
      toast.error(userFacingError(e, 'saveError'));
    } finally {
      setSaving(false);
    }
  };

  const fmtBrl = (cents) =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const selectedTier = tiers.find((tier) => tier.code === tierCode) || null;
  const usedSeats = seats?.used ?? null;
  const tierBelowUsage =
    selectedTier?.max_users != null && usedSeats != null && usedSeats > selectedTier.max_users;

  if (loading) {
    return (
      <div className="data-list-card flex items-center justify-center gap-2 p-10 text-gray-600">
        <Loader2 className="h-5 w-5 animate-spin" />
        Carregando plano…
      </div>
    );
  }

  if (!orgId) {
    return (
      <div className="data-list-card p-6 text-sm text-amber-800">
        Não foi possível identificar a organização (tenant). Acesse pelo domínio configurado ou defina a org no
        login.
      </div>
    );
  }

  return (
    <div className="data-list-card">
      {headerBar(
        CreditCard,
        'Plano e módulos (SaaS)',
        `Cobrança recorrente via ${providerLabel}, quando habilitada no back-end.`,
      )}
      <div className="p-5 sm:p-6 space-y-6">
        {subFetchError && (
          <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3">
            Não foi possível consultar o registro de assinatura. Confira se o subscription-service está no ar e se o
            gateway encaminha <span className="font-mono text-xs">/api/v1/subscriptions/organization/…</span>.
          </p>
        )}
        {!sub && !subFetchError && (
          <p className="text-sm text-gray-600">
            Ainda não há registro de assinatura para esta organização. Escolha os módulos e a periodicidade e use
            <strong> Salvar</strong> para criar a assinatura aqui.
          </p>
        )}
        {sub && (
          <div className="text-sm text-gray-600 space-y-2">
            <div>
              Status: <span className="font-medium text-gray-900">{sub.status}</span>
              {sub.billing_cycle ? (
                <>
                  {' '}
                  · Ciclo: <span className="font-medium text-gray-900">{sub.billing_cycle}</span>
                </>
              ) : null}
              {sub.price_period_cents != null ? (
                <>
                  {' '}
                  · Próxima fatura (referência): {fmtBrl(sub.price_period_cents)}
                </>
              ) : null}
              {sub.next_due_at ? (
                <>
                  {' '}
                  · Vencimento: {new Date(sub.next_due_at).toLocaleDateString('pt-BR')}
                </>
              ) : null}
            </div>
            {(sub.status === 'past_due' || sub.status === 'suspended') && (
              <div className="rounded-lg bg-amber-50 p-3 text-amber-900">
                {sub.status === 'suspended'
                  ? 'Assinatura suspensa — o uso do sistema está bloqueado até a regularização.'
                  : 'Pagamento em atraso — regularize antes do fim do período de graça para evitar bloqueio.'}
              </div>
            )}
            {canPay && (
              <div className="rounded-lg border border-gray-100 bg-gray-50/70 p-3 space-y-3">
                <p className="text-sm text-gray-700">Gere o Pix ou o boleto aqui. Não é necessário abrir o portal do Asaas.</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={`btn btn-sm inline-flex items-center gap-1 ${invoice?.method === 'pix' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => charge('pix')}
                    disabled={Boolean(charging)}
                  >
                    {charging === 'pix' ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                    {charging === 'pix' ? t('generatingPix') : t('payWithPix')}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm inline-flex items-center gap-1 ${invoice?.method === 'boleto' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => charge('boleto')}
                    disabled={Boolean(charging)}
                  >
                    {charging === 'boleto' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                    {charging === 'boleto' ? t('generatingBoleto') : t('payWithBoleto')}
                  </button>
                </div>
                {invoice?.method === 'pix' && (invoice.pix_copy || invoice.pix_qr_base64) && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600">{t('waitingPix')}</p>
                    {invoice.pix_qr_base64 ? <img className="h-40 w-40" src={qrSrc(invoice.pix_qr_base64)} alt="" /> : null}
                    {invoice.pix_copy ? (
                      <>
                        <code className="block break-all rounded bg-white p-2 text-xs">{invoice.pix_copy}</code>
                        <button type="button" className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={() => copyText(invoice.pix_copy)}>
                          <Copy className="h-4 w-4" /> {t('copyPix')}
                        </button>
                      </>
                    ) : null}
                  </div>
                )}
                {invoice?.method === 'boleto' && (invoice.boleto_linha || invoice.boleto_pdf_url) && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-600">{t('waitingBoleto')}</p>
                    {invoice.boleto_linha ? (
                      <>
                        <code className="block break-all rounded bg-white p-2 text-xs">{invoice.boleto_linha}</code>
                        <button type="button" className="btn btn-primary btn-sm inline-flex items-center gap-1" onClick={() => copyText(invoice.boleto_linha)}>
                          <Copy className="h-4 w-4" /> {t('copyBoleto')}
                        </button>
                      </>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className="btn btn-outline btn-sm inline-flex items-center gap-1" onClick={viewBoleto}>
                        <Eye className="h-4 w-4" /> {t('viewBoleto')}
                      </button>
                      <button type="button" className="btn btn-outline btn-sm inline-flex items-center gap-1" onClick={downloadBoleto}>
                        <Download className="h-4 w-4" /> {t('downloadBoleto')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {viewerOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
                <div className="flex h-[85vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl">
                  <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="font-semibold">Boleto da assinatura</h2>
                    <button type="button" className="rounded p-2 text-gray-500 hover:bg-gray-100" onClick={() => setViewerOpen(false)}>
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="min-h-0 flex-1 p-4">
                    {viewerUrl ? (
                      <iframe title="Boleto da assinatura" src={viewerUrl} className="h-full w-full rounded border" />
                    ) : (
                      <p>Carregando PDF…</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {pendingChanges.length > 0 && (
          <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-sm text-blue-900 space-y-2">
            <p className="font-medium">Mudanças agendadas</p>
            {pendingChanges.map((ch) => (
              <div key={ch.id} className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {ch.change_type} em {ch.effective_at ? new Date(ch.effective_at).toLocaleDateString('pt-BR') : '—'}
                </span>
                <button type="button" className="text-blue-800 underline text-xs" onClick={() => cancelChange(ch.id)}>
                  Cancelar
                </button>
              </div>
            ))}
          </div>
        )}

        {pricingFromFallback && (
          <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-3">
            Preços de módulo não puderam ser carregados do servidor; exibindo tabela padrão.
          </p>
        )}

        <div className="form-group max-w-md">
          <label className="form-label" htmlFor="sub-cycle">
            Periodicidade
          </label>
          <select
            id="sub-cycle"
            className="form-select"
            value={cycle}
            onChange={(e) => setCycle(e.target.value)}
          >
            {CYCLES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group max-w-md">
          <label className="form-label" htmlFor="sub-user-tier">
            Faixa de usuários
          </label>
          <select
            id="sub-user-tier"
            className="form-select"
            value={tierCode}
            onChange={(e) => setTierCode(e.target.value)}
          >
            <option value="">Sem faixa definida (sem limite)</option>
            {tiers.map((tier) => (
              <option key={tier.code} value={tier.code}>
                {tier.display_name}
                {tier.price_monthly_cents ? ` · +${fmtBrl(tier.price_monthly_cents)}/mês` : ' · incluído'}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-gray-500">
            Conta apenas usuários internos (servidores e equipe). Requerentes que acessam o portal não ocupam vaga.
          </p>
          {seats && (
            <p className="mt-1 text-xs text-gray-600">
              Em uso hoje: <strong>{seats.used}</strong>
              {seats.max_users != null ? ` de ${seats.max_users} vagas contratadas` : ' (sem limite contratado)'}.
            </p>
          )}
          {tierBelowUsage && (
            <p className="mt-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              Esta faixa permite {selectedTier.max_users} usuários e a organização já tem {usedSeats} ativos. Desative
              usuários antes de reduzir a faixa.
            </p>
          )}
        </div>

        <div>
          <h4 className="mb-3 text-sm font-semibold text-gray-900">Módulos contratados</h4>
          <div className="space-y-2">
            {pricing
              .filter((p) => p.is_active !== false)
              .map((p) => {
                const code = String(p.code).toLowerCase();
                const on = selected.has(code);
                return (
                  <label
                    key={p.code}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-2"
                  >
                    <input
                      type="checkbox"
                      className="form-check mt-1"
                      checked={on}
                      disabled={code === 'base'}
                      onChange={() => toggle(p.code)}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{p.display_name || p.code}</div>
                      <div className="text-xs text-gray-500">
                        {p.code} · {fmtBrl(p.price_monthly_cents)}/mês
                      </div>
                    </div>
                  </label>
                );
              })}
            {pricing.length === 0 && <p className="text-sm text-gray-500">Nenhum preço de módulo retornado.</p>}
          </div>
        </div>

        {previewCents != null && (
          <p className="text-sm text-gray-700">
            Próxima referência (período): <strong>{fmtBrl(previewCents)}</strong>
          </p>
        )}

        {planChangePreview && planChangePreview.change_type !== 'noop' && (
          <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{planChangePreview.message}</p>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={saving || tierBelowUsage}
          >
            {saving ? 'Salvando…' : sub ? 'Salvar plano' : 'Criar assinatura e salvar plano'}
          </button>
          <button type="button" className="btn btn-outline" onClick={load} disabled={saving}>
            Recarregar
          </button>
        </div>
      </div>
    </div>
  );
}
